import client from '../ApolloClient'
import gql from 'graphql-tag'

import {
  FETCH_ADMIN_DETAILS,
  FETCH_ALERT_DETAILS,
  FETCH_CONTENT_DETAILS,
  FETCH_NOTIFICATION_DETAILS,
  FETCH_NOTIFICATION_USERS,
  FETCH_USER_DETAILS,
  LOAD_NOTIFICATION_USERS,
  loadAdminDetails,
  loadAlertDetails,
  loadContentDetails,
  loadNotificationDetails,
  loadNotificationUsers,
  loadUserDetails
} from '../Actions/Details'

import { failFetch } from '../Actions/Other'
// eslint-disable-next-line
import { filter, map, mergeMap, Observable, Subject, switchMap } from 'rxjs'

const userDetailsQuery = gql`query userDetails($accountId: String!,
      $startDate: DateTime!, $endDate: DateTime!, $userId: String!) {
  getUserDetails(accountId: $accountId, startDate: $startDate, endDate: $endDate userId: $userId) {
    user {
      _id
      name
      email
    }
    recentActivity
    lastLogin
    avgDurationInApp
    avgDurationInCMS
    topSearches
    recentViews {
      title
      totalViews
      avgViewDuration
      smallThumbnailUrl
    }
    profileNames
    groupNames
  }
}`

/**
  * userDetailsEpic
  * @desc action Observer that sends a query and processes result if the action is a user details action
  * @param {Observable<action>} action$ Action Observable stream
  * @return {action}
*/
export const userDetailsEpic = action$ =>
  action$.pipe(
    filter(action => action.type === FETCH_USER_DETAILS),
    mergeMap(action => {
      const subject = new Subject()
      client.query({ query: userDetailsQuery, variables: action.query })
        .then((data) => { subject.next(data) })
        .catch((error) => {
          subject.next(error)
        })
      return subject.pipe(
        map((response) => {
          if (response.data) {
            return loadUserDetails(response.data)
          } else {
            return failFetch(response)
          }
        })
      )
    })
  )

const notificationDetailsQuery = gql`query notificationDetails($accountId: String!, $pushNotificationType: PushNotificationType!, $documentId: String!) {
  getNotificationDetails(accountId: $accountId, pushNotificationType: $pushNotificationType, documentId: $documentId) {
    title
    lastOneSignalAPIUpdated
    createdDate
    documentId
    notificationType
    deliveryData {
      totalSent
      totalDelivered
      totalFailed
      totalErrored
      confirmed
    }
    platformData {
      totalDelivered
      ios
      android
    }
  }
}`

const notificationUsersQuery = gql`query getNotificationUsers($accountId: String!, $pushNotificationType: PushNotificationType!, $documentId: String!, $limit: Int!, $offset: Int!) {
  getNotificationUsers (accountId: $accountId, pushNotificationType: $pushNotificationType, documentId: $documentId, limit: $limit, offset: $offset) {
    users {
      _id
      name
      regCode
      email
    }
    count
  }
}`

/**
  * notificationsDetailsEpic
  * @desc action Observer that sends a query and processes result if the action is a notification details action
  * @param {Observable<action>} action$ Action Observable stream
  * @return {action}
*/
export const notificationsDetailsEpic = action$ =>
  action$.pipe(
    filter(action => action.type === FETCH_NOTIFICATION_DETAILS),
    mergeMap(action => {
      const subject = new Subject()
      client.query({ query: notificationDetailsQuery, variables: action.query })
        .then((data) => { subject.next(data) })
        .catch((error) => {
          subject.next(error)
        })
      return subject.pipe(
        map((response) => {
          if (response.data) {
            return loadNotificationDetails(response.data)
          } else {
            return failFetch(response)
          }
        })
      )
    })
  )

const contentDetailsQuery = gql`query contentSummary($accountId: String!,
      $contentId: String!, $startDate: DateTime!, $endDate: DateTime!,
    $appProfileIds: [ID!], $groupIds: [ID!], $appType: AppType!) {
  contentSummary(accountId: $accountId, contentId: $contentId, startDate: $startDate, endDate: $endDate, appProfileIds: $appProfileIds, groupIds: $groupIds, appType: $appType ) {
    title
    notify
    thumbnailUrl
    created
    totalViews
    totalViewsPctChange
    totalShares
    totalSharesPctChange
    totalViewsNotifications
    totalViewsNotificationsPctChange
    avgViewDuration
    avgViewDurationPctChange
    avgDurationInApp
    avgDurationInCMS
    pctChangedInCMS
    pctChangedInApp
    uniqueViews
    uniqueViewsPctChange
    totalOfflineDownloads
    totalOfflineDownloadsPctChange
    totalLikes
    totalReactions
    totalComments
    likesEnabled
    commentsEnabled
    mentionsEnabled
    mentions {
      _id
      name
    }
    hashtags
    accountDetails {
      _id
      name
      ui_version
    }
  }
}`

/**
  * contentDetailsEpic
  * @desc action Observer that sends a query and processes result if the action is a content details action
  * @param {Observable<action>} action$ Action Observable stream
  * @return {action}
*/
export const contentDetailsEpic = action$ =>
  action$.pipe(
    filter(action => action.type === FETCH_CONTENT_DETAILS),
    mergeMap(action => {
      const subject = new Subject()
      client.query({ query: contentDetailsQuery, variables: action.query })
        .then((data) => { subject.next(data) })
        .catch((error) => {
          subject.next(error)
        })
      return subject.pipe(
        map((response) => {
          if (response.data) {
            return loadContentDetails(response.data)
          } else {
            return failFetch(response)
          }
        })
      )
    })
  )

const alertDetailsQuery = gql`query alertSummary($accountId: String!,
      $alertId: String!, $startDate: DateTime!, $endDate: DateTime!,
    $appProfileIds: [ID!], $groupIds: [ID!], $appType: AppType!) {
    alertSummary(accountId: $accountId, alertId: $alertId, startDate: $startDate, endDate: $endDate, appProfileIds: $appProfileIds, groupIds: $groupIds, appType: $appType ) {
    alert_message
    created
    totalViews
    totalViewsPctChange
    uniqueViews
    uniqueViewsPctChange
    totalViewsNotifications
  }
}`

/**
  * alertDetailsEpic
  * @desc action Observer that sends a query and processes result if the action is a alert details action
  * @param {Observable<action>} action$ Action Observable stream
  * @return {action}
*/
export const alertDetailsEpic = action$ =>
  action$.pipe(
    filter(action => action.type === FETCH_ALERT_DETAILS),
    mergeMap(action => {
      const subject = new Subject()
      client.query({ query: alertDetailsQuery, variables: action.query })
        .then((data) => { subject.next(data) })
        .catch((error) => {
          subject.next(error)
        })
      return subject.pipe(
        map((response) => {
          if (response.data) {
            return loadAlertDetails(response.data)
          } else {
            return failFetch(response)
          }
        })
      )
    })
  )

const adminDetailsQuery = gql`query getAdminDetails(
  $accountId: String!,
  $userId: String!,
  $startDate: DateTime!,
  $endDate: DateTime!) {
    getAdminDetails( accountId: $accountId, userId: $userId, startDate: $startDate, endDate: $endDate) {
      name
      adminType
      recentActivity
      lastLogin
      totalContentAdded
      recentFoldersAdded {
        title
        totalViews
      }
      recentContentAdded {
        _id
        title
        created
        thumbnailUrl
        avgViewDuration
        totalViews
      }
    }
  }`

/**
  * adminDetailsEpic
  * @desc action Observer that sends a query and processes result if the action is an admin details action
  * @param {Observable<action>} action$ Action Observable stream
  * @return {action}
*/
export const adminDetailsEpic = action$ =>
  action$.pipe(
    filter(action => action.type === FETCH_ADMIN_DETAILS),
    mergeMap(action => {
      const subject = new Subject()
      client.query({ query: adminDetailsQuery, variables: action.query })
        .then((data) => { subject.next(data) })
        .catch((error) => {
          subject.next(error)
        })
      return subject.pipe(
        map((response) => {
          if (response.data) {
            return loadAdminDetails(response.data)
          } else {
            return failFetch(response)
          }
        })
      )
    })
  )

/**
 * notificationUsersEpic
 * @desc action Observer that sends a query and processes result if the action is a a users notification action
 * @param {Observable<action>} action$ Action Observable stream
 * @return {action}
 */
export const notificationUsersEpic = action$ =>
  action$.pipe(
    filter(action => action.type === FETCH_NOTIFICATION_USERS),
    switchMap(action => {
      const subject = new Subject()
      client.query({ query: notificationUsersQuery, variables: action.query })
        .then((data) => { subject.next(data) })
        .catch((error) => {
          subject.next(error)
        })
      return subject.pipe(
        map((response) => {
          if (response.data) {
            return loadNotificationUsers(response.data, LOAD_NOTIFICATION_USERS)
          } else {
            return failFetch(response)
          }
        })
      )
    })
  )
